import { DialogComponent } from '@theme/dialog';
import { fetchConfig } from '@theme/utilities';

/**
 * A custom element that manages the "back in stock" alert drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} formWrapper - The alert form wrapper.
 * @property {HTMLInputElement} emailInput - The email input.
 * @property {HTMLInputElement} marketingConsent - The marketing consent checkbox.
 * @property {HTMLButtonElement} submitButton - The submit button.
 * @property {HTMLElement} errorMessage - The error message element.
 * @property {HTMLElement} successMessage - The success message element.
 * @property {HTMLElement} subheading - The subheading element.
 *
 * @extends {DialogComponent}
 */
class StockAlertDrawerComponent extends DialogComponent {
  requiredRefs = ['dialog', 'formWrapper', 'emailInput'];

  /** @type {number | undefined} */
  #autoCloseTimeout;

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#autoCloseTimeout);
  }

  /**
   * Opens the drawer for a given variant, e.g. from an out-of-stock variant button,
   * without changing the variant selected on the page.
   *
   * @param {object} options
   * @param {string} options.variantId - The ID of the variant to subscribe to.
   * @param {string} [options.subheading] - The subheading text for this variant.
   * @param {string} [options.successMessage] - The success message for this variant.
   */
  openForVariant({ variantId, subheading, successMessage }) {
    this.dataset.variantId = variantId;
    if (successMessage) this.dataset.successMessage = successMessage;
    if (subheading && this.refs.subheading) this.refs.subheading.textContent = subheading;

    clearTimeout(this.#autoCloseTimeout);
    this.#resetForm();
    this.showDialog();
  }

  /**
   * Submits the alert on Enter keydown from the email input.
   *
   * @param {KeyboardEvent} event
   */
  handleKeyDown = (event) => {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    this.handleSubmit(event);
  };

  /**
   * Handles the alert submission.
   *
   * @param {Event} event
   */
  handleSubmit = async (event) => {
    event.preventDefault();

    const { emailInput, marketingConsent, submitButton } = this.refs;

    if (!emailInput.checkValidity()) {
      emailInput.reportValidity();
      return;
    }

    this.#setError('');
    submitButton.setAttribute('aria-busy', 'true');
    submitButton.disabled = true;

    try {
      const response = await fetch(
        this.dataset.endpoint ?? '/apps/stock-alerts/subscribe',
        fetchConfig('json', {
          body: JSON.stringify({
            productVariantId: String(this.dataset.variantId),
            email: emailInput.value,
            countryCode: this.dataset.countryCode ?? '',
            acceptsEmailMarketing: Boolean(marketingConsent?.checked),
          }),
        })
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        this.#setError(data.error || this.dataset.errorMessage);
        return;
      }

      this.#showSuccess();
    } catch (error) {
      console.error(error);
      this.#setError(this.dataset.errorMessage);
    } finally {
      submitButton.removeAttribute('aria-busy');
      submitButton.disabled = false;
    }
  };

  /**
   * @param {string} message
   */
  #setError(message) {
    const { errorMessage } = this.refs;
    if (!errorMessage) return;

    errorMessage.textContent = message ?? '';
    errorMessage.hidden = !message;
  }

  #showSuccess() {
    const { formWrapper, emailInput, marketingConsent, successMessage } = this.refs;

    this.#setError('');
    formWrapper.hidden = true;

    if (successMessage) {
      successMessage.textContent = this.dataset.successMessage ?? '';
      successMessage.hidden = false;
    }

    clearTimeout(this.#autoCloseTimeout);
    this.#autoCloseTimeout = window.setTimeout(async () => {
      await this.closeDialog();
      emailInput.value = '';
      if (marketingConsent) marketingConsent.checked = false;
      this.#resetForm();
    }, 7000);
  }

  #resetForm() {
    const { formWrapper, successMessage } = this.refs;

    this.#setError('');
    formWrapper.hidden = false;
    if (successMessage) successMessage.hidden = true;
  }
}

if (!customElements.get('stock-alert-drawer-component')) {
  customElements.define('stock-alert-drawer-component', StockAlertDrawerComponent);
}
